import { Box, HStack, Stack, Table } from "@chakra-ui/react";
import { CustomPagination } from "../components/ui/custom-pagination.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Loader from "../components/loader.tsx";
import { FaTrashAlt } from "react-icons/fa";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog.tsx";
import { Button } from "../components/ui/button.tsx";
import { api } from "../api/custom-api.ts";
import { usePagination } from "../hooks";

export const EmployeesPage = () => {
  const pageSize = 10;

  const {
    pageIndex,
    setPage,
    nextPage: onNextPage,
    previousPage: onPreviousPage,
    canPreviousPage,
  } = usePagination({
    pageType: "employees",
    pageSize,
  });

  const { data, isLoading } = useQuery(
    ["users", pageIndex, pageSize],
    () => api.user.getWarehouseUsers(pageSize, pageIndex * pageSize),
    {
      keepPreviousData: true,
    },
  );

  // Calculate canNextPage based on data
  const canNextPage = data ? pageIndex < data.totalPages - 1 : false;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box
      w="100%"
      h="full"
      pt="5.25rem"
      p="1rem"
      bg="white"
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <HStack
        w="full"
        justify="space-between"
        pt="5.25rem"
        mb="1rem"
        flexWrap="wrap"
      >
        <Box fontSize={["lg", "xl", "2xl"]} fontWeight="semibold" color="black">
          ZAPOSLENI
        </Box>
      </HStack>
      <Stack width="full" gap="5">
        <Box overflowX="auto">
          <Table.Root
            size="sm"
            stickyHeader
            interactive
            variant="outline"
            minWidth="800px"
          >
            <Table.Header bg="gray.200">
              <Table.Row>
                <Table.ColumnHeader
                  key={1}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  IME
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={2}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  PREZIME
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={3}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  EMAIL ADRESA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={4}
                  textAlign="center"
                  className="text-black font-semibold"
                ></Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data?.items?.map((item) => (
                <Table.Row
                  key={item.id}
                  className="hover:bg-gray-100 cursor-pointer"
                >
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.firstName}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.lastName}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.email}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    <Dialog
                      id={item.id}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <CustomPagination
            count={data!.totalItems}
            pageSize={10}
            currentPage={pageIndex}
            onPageChange={setPage}
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            siblingCount={1}
          />
        </Box>
      </Stack>
    </Box>
  );
};

const Dialog = ({
  id,
  pageIndex,
  pageSize,
}: {
  id: string;
  pageIndex: number;
  pageSize: number;
}) => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (id: string) => api.user.deleteUserById(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users", pageIndex, pageSize],
      });
    },
  });

  return (
    <DialogRoot role="alertdialog">
      <DialogTrigger asChild>
        <FaTrashAlt color={"black"} className={"cursor-pointer"} />
      </DialogTrigger>
      <DialogContent className={"bg-[#ffffff]"}>
        <DialogHeader>
          <DialogTitle className={"text-red-500 font-semibold text-[24px]"}>
            Upozorenje
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className={"text-black"}>
            Klikom na dugme "Potvrdi" obrisaćete zaposlenog iz baze podataka,
            ukoliko to ne želite kliknite na dugme "Odustani".
          </p>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              size={"sm"}
              colorPalette={"red"}
              width={"5rem"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              variant="outline"
            >
              Odustani
            </Button>
          </DialogActionTrigger>
          <Button
            size={"sm"}
            colorPalette={"red"}
            width={"5rem"}
            color={"#EF4444"}
            backgroundColor={"#ffffff"}
            borderColor={"#ffffff"}
            fontWeight={"600"}
            fontSize={"14px"}
            borderRadius={"0.5rem"}
            variant={"outline"}
            onClick={() => mutate(id)}
          >
            Potvrdi
          </Button>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};
